import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { workOrdersAPI, clientsAPI, jobsAPI } from '../../lib/api';
import { toast } from 'sonner';

const COMPANIES = ['TriCorps Security', 'TriCorps Surveillance',
  'TriCorps Cybersecurity', 'TriCorps Government'];

const EMPTY = {
  type: 'open', client_id: '', new_client_name: '',
  billing_contact_first_name: '', billing_contact_last_name: '',
  billing_contact_email: '', billing_contact_phone: '', net_term_days: '',
  company: 'TriCorps Security', job_id: '', site_name: '',
  site_address1: '', site_address2: '', site_city: '', site_state: '', site_zip: '',
  site_contact_first_name: '', site_contact_last_name: '',
  site_contact_phone: '', site_contact_email: '',
  officer_classifications: [], requested_pay_rate: '', requested_bill_rate: '',
  holidays_billable: false, taxable: false, po_number: '', date_to_start: '',
  additional_services: false, additional_services_detail: '', has_equipment: false,
  notes: '', additional_information: '', directions: '', keys_instructions: '',
};

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs uppercase tracking-wide text-fg-muted">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-xs text-fg-muted mt-1">{hint}</p>}
  </div>
);

const YesNo = ({ label, value, onChange, testId }) => (
  <label className="flex items-center justify-between gap-3 text-sm py-1.5">
    <span className="text-fg">{label}</span>
    <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

/** The manager's work-order form. Mirrors the current public form field for field. */
export const WorkOrderForm = ({ open, onOpenChange, meta, existing, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [newClient, setNewClient] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  const isResubmit = !!existing?.id;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [c, j] = await Promise.all([clientsAPI.list(), jobsAPI.list()]);
        setClients(c.data || []);
        setJobs(j.data || []);
      } catch { /* selects degrade to empty */ }
    })();
    if (isResubmit) {
      setForm({
        ...EMPTY, ...existing,
        client_id: existing.client_id || '',
        requested_pay_rate: existing.requested_pay_rate_cents != null
          ? (existing.requested_pay_rate_cents / 100).toFixed(2) : '',
        requested_bill_rate: existing.requested_bill_rate_cents != null
          ? (existing.requested_bill_rate_cents / 100).toFixed(2) : '',
        site_name: existing.job?.site_name || '',
        site_address1: existing.job?.site_address1 || '',
        site_address2: existing.job?.site_address2 || '',
        site_city: existing.job?.site_city || '',
        site_state: existing.job?.site_state || '',
        site_zip: existing.job?.site_zip || '',
        site_contact_first_name: existing.job?.site_contact_first_name || '',
        site_contact_last_name: existing.job?.site_contact_last_name || '',
        site_contact_email: existing.job?.site_contact_email || '',
        site_contact_phone: (existing.job?.site_phones || [])[0]?.phone || '',
        directions: existing.job?.directions || '',
        keys_instructions: existing.job?.keys_instructions || '',
        company: existing.job?.company || 'TriCorps Security',
        officer_classifications: existing.officer_classifications || [],
      });
      setNewClient(!!existing.new_client_name);
    } else {
      setForm(EMPTY);
      setNewClient(false);
      setFile(null);
    }
  }, [open, existing, isResubmit]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleClass = (value) => set({
    officer_classifications: form.officer_classifications.includes(value)
      ? form.officer_classifications.filter((v) => v !== value)
      : [...form.officer_classifications, value],
  });

  const save = async () => {
    if (form.type === 'close' && !form.job_id) {
      toast.error('Choose the job you are asking to close');
      return;
    }
    if (form.type === 'open') {
      if (!newClient && !form.client_id) {
        toast.error('Choose an existing client, or switch on "New client"');
        return;
      }
      if (newClient && !form.new_client_name.trim()) {
        toast.error('Enter the new client name');
        return;
      }
      if (!form.site_name.trim()) {
        toast.error('Enter the well / site name');
        return;
      }
    }
    setSaving(true);
    const body = {
      ...form,
      client_id: newClient ? null : (form.client_id || null),
      new_client_name: newClient ? form.new_client_name : null,
      requested_pay_rate: form.requested_pay_rate === '' ? null : Number(form.requested_pay_rate),
      requested_bill_rate: form.requested_bill_rate === '' ? null : Number(form.requested_bill_rate),
      date_to_start: form.date_to_start || null,
    };
    try {
      const res = isResubmit
        ? await workOrdersAPI.resubmit(existing.id, body)
        : await workOrdersAPI.submit(body);
      if (file) {
        try { await workOrdersAPI.uploadAttachment(res.data.id, file); }
        catch { toast.error('Work order saved, but the attachment failed to upload'); }
      }
      toast.success(res.data.status === 'approved'
        ? `${res.data.wo_number} created`
        : `${res.data.wo_number} submitted for approval`);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the work order');
    } finally {
      setSaving(false);
    }
  };

  const openJobs = useMemo(() => jobs.filter((j) => j.is_active !== false), [jobs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin"
        data-testid="work-order-form">
        <DialogHeader>
          <DialogTitle>
            {isResubmit ? `Correct and resubmit ${existing.wo_number}` : 'New work order'}
          </DialogTitle>
        </DialogHeader>

        {isResubmit && existing?.review?.comments && (
          <div className="rounded-md border border-red-300 bg-red-50 p-3"
            data-testid="work-order-rejection-note">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
              Why it was sent back
            </p>
            <p className="text-sm text-red-800 mt-1">
              {existing.review.comments}
            </p>
          </div>
        )}

        <div className="space-y-5">
          {!isResubmit && (
            <Field label="Work order type">
              <Select value={form.type} onValueChange={(v) => set({ type: v })}>
                <SelectTrigger data-testid="wo-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open — stand up a new site</SelectItem>
                  <SelectItem value="close">Close — shut down an existing site</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {form.type === 'close' ? (
            <Field label="Job to close" hint="Approval deactivates the job.">
              <Select value={form.job_id} onValueChange={(v) => set({ job_id: v })}>
                <SelectTrigger data-testid="wo-close-job">
                  <SelectValue placeholder="Choose a job" />
                </SelectTrigger>
                <SelectContent>
                  {openJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.job_number} — {j.site_name || j.job_description || 'Site'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : (
            <>
              <div className="rounded-md border border-line p-3 space-y-3">
                <YesNo label="This is a client we don't have yet"
                  value={newClient} onChange={setNewClient} testId="wo-new-client-toggle" />
                {newClient ? (
                  <Field label="New client name"
                    hint="Finance assigns the customer number when they review this.">
                    <Input value={form.new_client_name}
                      onChange={(e) => set({ new_client_name: e.target.value })}
                      data-testid="wo-new-client-name" />
                  </Field>
                ) : (
                  <Field label="Client">
                    <Select value={form.client_id} onValueChange={(v) => set({ client_id: v })}>
                      <SelectTrigger data-testid="wo-client">
                        <SelectValue placeholder="Choose a client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Billing contact first name">
                    <Input value={form.billing_contact_first_name}
                      onChange={(e) => set({ billing_contact_first_name: e.target.value })}
                      data-testid="wo-billing-first" />
                  </Field>
                  <Field label="Billing contact last name">
                    <Input value={form.billing_contact_last_name}
                      onChange={(e) => set({ billing_contact_last_name: e.target.value })}
                      data-testid="wo-billing-last" />
                  </Field>
                  <Field label="Billing email">
                    <Input value={form.billing_contact_email} type="email"
                      onChange={(e) => set({ billing_contact_email: e.target.value })}
                      data-testid="wo-billing-email" />
                  </Field>
                  <Field label="Billing phone">
                    <Input value={form.billing_contact_phone}
                      onChange={(e) => set({ billing_contact_phone: e.target.value })}
                      data-testid="wo-billing-phone" />
                  </Field>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="TriCorps company">
                  <Select value={form.company} onValueChange={(v) => set({ company: v })}>
                    <SelectTrigger data-testid="wo-company"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMPANIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Well / site name">
                  <Input value={form.site_name}
                    onChange={(e) => set({ site_name: e.target.value })}
                    data-testid="wo-site-name" />
                </Field>
                <Field label="Street address">
                  <Input value={form.site_address1}
                    onChange={(e) => set({ site_address1: e.target.value })}
                    data-testid="wo-site-address1" />
                </Field>
                <Field label="Address line 2">
                  <Input value={form.site_address2}
                    onChange={(e) => set({ site_address2: e.target.value })}
                    data-testid="wo-site-address2" />
                </Field>
                <Field label="City">
                  <Input value={form.site_city}
                    onChange={(e) => set({ site_city: e.target.value })}
                    data-testid="wo-site-city" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="State">
                    <Input value={form.site_state} maxLength={2}
                      onChange={(e) => set({ site_state: e.target.value.toUpperCase() })}
                      data-testid="wo-site-state" />
                  </Field>
                  <Field label="ZIP">
                    <Input value={form.site_zip} maxLength={10}
                      onChange={(e) => set({ site_zip: e.target.value })}
                      data-testid="wo-site-zip" />
                  </Field>
                </div>
                <Field label="Site contact first name (operations)">
                  <Input value={form.site_contact_first_name}
                    onChange={(e) => set({ site_contact_first_name: e.target.value })}
                    data-testid="wo-site-contact-first" />
                </Field>
                <Field label="Site contact last name (operations)">
                  <Input value={form.site_contact_last_name}
                    onChange={(e) => set({ site_contact_last_name: e.target.value })}
                    data-testid="wo-site-contact-last" />
                </Field>
                <Field label="Site contact phone">
                  <Input value={form.site_contact_phone}
                    onChange={(e) => set({ site_contact_phone: e.target.value })}
                    data-testid="wo-site-contact-phone" />
                </Field>
                <Field label="Site contact email">
                  <Input value={form.site_contact_email} type="email"
                    onChange={(e) => set({ site_contact_email: e.target.value })}
                    data-testid="wo-site-contact-email" />
                </Field>
              </div>

              <Field label="Security officer classification for site"
                hint="Choose as many as apply. If none fit, describe it in the notes.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1">
                  {(meta?.officer_classifications || []).map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={form.officer_classifications.includes(c)}
                        onCheckedChange={() => toggleClass(c)}
                        data-testid={`wo-class-${c.replace(/[^a-z]+/gi, '-').toLowerCase()}`} />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Pay rate ($/hr)"
                  hint="Only Finance and payroll holders can read this back.">
                  <Input value={form.requested_pay_rate} type="number" step="0.01" min="0"
                    onChange={(e) => set({ requested_pay_rate: e.target.value })}
                    data-testid="wo-pay-rate" />
                </Field>
                <Field label="Bill rate ($/hr)">
                  <Input value={form.requested_bill_rate} type="number" step="0.01" min="0"
                    onChange={(e) => set({ requested_bill_rate: e.target.value })}
                    data-testid="wo-bill-rate" />
                </Field>
                <Field label="PO number (if available)">
                  <Input value={form.po_number}
                    onChange={(e) => set({ po_number: e.target.value })}
                    data-testid="wo-po-number" />
                </Field>
                <Field label="Net / term days">
                  <Input value={form.net_term_days} placeholder="Net 30"
                    onChange={(e) => set({ net_term_days: e.target.value })}
                    data-testid="wo-net-terms" />
                </Field>
                <Field label="Start date">
                  <Input value={form.date_to_start || ''} type="date"
                    onChange={(e) => set({ date_to_start: e.target.value })}
                    data-testid="wo-start-date" />
                </Field>
              </div>

              <div className="rounded-md border border-line p-3 divide-y divide-line-subtle">
                <YesNo label="Pay / bill holidays?" value={form.holidays_billable}
                  onChange={(v) => set({ holidays_billable: v })} testId="wo-holidays" />
                <YesNo label="Is this work order taxable?" value={form.taxable}
                  onChange={(v) => set({ taxable: v })} testId="wo-taxable" />
                <YesNo label="Any additional services provided?" value={form.additional_services}
                  onChange={(v) => set({ additional_services: v })} testId="wo-additional-services" />
                <YesNo label="Does this site have equipment?" value={form.has_equipment}
                  onChange={(v) => set({ has_equipment: v })} testId="wo-has-equipment" />
              </div>

              {form.additional_services && (
                <Field label="Please specify the additional services">
                  <Textarea rows={2} value={form.additional_services_detail}
                    onChange={(e) => set({ additional_services_detail: e.target.value })}
                    data-testid="wo-additional-services-detail" />
                </Field>
              )}

              <Field label="Directions">
                <Textarea rows={2} value={form.directions}
                  onChange={(e) => set({ directions: e.target.value })}
                  data-testid="wo-directions" />
              </Field>
              <Field label="Keys / access instructions">
                <Textarea rows={2} value={form.keys_instructions}
                  onChange={(e) => set({ keys_instructions: e.target.value })}
                  data-testid="wo-keys" />
              </Field>
            </>
          )}

          <Field label="Notes">
            <Textarea rows={3} value={form.notes}
              onChange={(e) => set({ notes: e.target.value })} data-testid="wo-notes" />
          </Field>
          <Field label="Additional information">
            <Textarea rows={2} value={form.additional_information}
              onChange={(e) => set({ additional_information: e.target.value })}
              data-testid="wo-additional-info" />
          </Field>
          <Field label="Attachment"
            hint={`Optional. Up to ${meta?.max_upload_mb || 50} MB.`}>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="wo-file" />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="wo-submit">
            {isResubmit ? 'Resubmit for approval' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderForm;
